/**
 * The seat order a table is dealt in, from how the last round finished.
 *
 * Whoever held the MOST cards goes first and whoever went out FIRST goes last,
 * so the order is the last round's ranking read backwards. Seat order is turn
 * order, so this is also where each player sits: the decks move round the table
 * between rounds.
 *
 * The rules, in the order they are applied:
 *
 * - **A seat with no result goes first**, in the order given. Somebody who was
 *   only watching last round (a late joiner, a seat added between rounds) is
 *   seeded level with LAST place on the leaderboard, and last place is exactly
 *   who this rule puts at the front. It is also why a table's very first deal
 *   comes back unchanged: nobody has a result yet.
 * - **Then most cards left first.**
 * - **Players who went out come last, and the first out is the very last.**
 *   They all hold zero cards, so the card count cannot order them; the order
 *   they went out in (`outOrder`, i.e. `roundWinnerIds`) does, reversed. A
 *   player on zero who is missing from that list — a record written before the
 *   list existed — goes in front of everyone who is on it.
 * - **Any remaining tie keeps the order given**, which is what makes the result
 *   deterministic, including under a server transaction retry.
 *
 * `seatIds` is the order to fall back on, not a ranking: pass the table as it
 * was last round. A party passes one table's slice of the roster, and the ids
 * in `cardsLeft`/`outOrder` that belong to other tables are simply ignored —
 * which is what lets the party's pooled result order every table at once. Among
 * players who went out at DIFFERENT tables the list is table by table, not one
 * clock, so their order there is only as fair as "who is at the lower-numbered
 * table" — they finished level on points, which is the part that matters.
 *
 * Mirrors `TurnOrderFromRound` in the Go engine; the two have mirrored tests.
 */
export function turnOrderFromRound(
  seatIds: readonly string[],
  cardsLeft: Readonly<Record<string, number>> | null | undefined,
  outOrder: readonly string[] | null | undefined,
): string[] {
  const cards = cardsLeft ?? {};
  const outRank = new Map<string, number>();
  (outOrder ?? []).forEach((id, i) => {
    if (!outRank.has(id)) outRank.set(id, i);
  });
  const seatOf = new Map(seatIds.map((id, i) => [id, i]));

  // One sortable tuple per seat, compared left to right.
  //   group:  0 = no result, 1 = holding cards, 2 = out.
  //   within: bigger goes first — cards held, or how LATE they went out.
  const key = (id: string): [number, number] => {
    if (!(id in cards)) return [0, 0];
    const held = cards[id];
    if (held > 0) return [1, held];
    // Missing from the list: in front of everyone on it.
    return [2, outRank.has(id) ? outRank.get(id)! : Number.MAX_SAFE_INTEGER];
  };

  return [...seatIds].sort((a, b) => {
    const [ga, wa] = key(a);
    const [gb, wb] = key(b);
    if (ga !== gb) return ga - gb;
    if (wa !== wb) return wb - wa;
    return seatOf.get(a)! - seatOf.get(b)!;
  });
}
